import React, { useState, useCallback, useEffect, useRef } from 'react';
import { searchSpecies, createSpecies } from '../../services/speciesService';
import { SPECIES_CATEGORIES } from '../../utils/speciesCategories';

const POPULAR_COLOR = '#f8fafc';
const POPULAR_BORDER = '#cbd5e1';

function useDebounce(fn, delay) {
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  return useCallback((...args) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

export default function SpeciesAutocomplete({
  value = '',
  onSelect,
  onInputChange,
  popularSpecies = [],
  placeholder = 'Start typing species name…',
  id,
  disabled = false,
  canCreate = false,
}) {
  const [inputValue, setInputValue]   = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]               = useState(false);
  const [loading, setLoading]         = useState(false);
  const [activeIdx, setActiveIdx]     = useState(-1);
  const [creating, setCreating] = useState(false);
  const [newSpecies, setNewSpecies] = useState({ commonName: '', category: '', harvestCategory: '' });
  const [createBusy, setCreateBusy] = useState(false);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);
  const selectedNameRef = useRef(value || '');
  const blurTimerRef = useRef(null);
  const searchGeneration = useRef(0);
  useEffect(() => () => clearTimeout(blurTimerRef.current), []);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const doSearch = useCallback(async (q) => {
    const generation = ++searchGeneration.current;
    if (!q || q.trim().length < 2) { setSuggestions([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await searchSpecies(q.trim(), { limit: 10 });
      if (generation === searchGeneration.current) setSuggestions(res.data || []);
    } catch (err) {
      if (generation === searchGeneration.current) {
        setSuggestions([]);
        setError(err.response?.data?.message || 'Species search failed. Retry before selecting a catalogue entry.');
      }
    } finally {
      if (generation === searchGeneration.current) setLoading(false);
    }
  }, []);

  const debouncedSearch = useDebounce(doSearch, 250);

  const handleInput = (e) => {
    searchGeneration.current += 1;
    setSuggestions([]);
    setLoading(false);
    setError('');
    const v = e.target.value;
    selectedNameRef.current = '';
    setInputValue(v);
    setActiveIdx(-1);
    debouncedSearch(v);
    if (!v.trim()) {
      setSuggestions([]);
      setOpen(false);
      onSelect({ speciesId: null, speciesName: '' });
      return;
    }
    onInputChange?.(v);
    setOpen(true);
  };

  const handleSelect = (item) => {
    searchGeneration.current += 1;
    setLoading(false);
    selectedNameRef.current = item.commonName;
    setInputValue(item.commonName);
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
    onSelect({ speciesId: item._id, speciesName: item.commonName, harvestCategory: item.harvestCategory || '', speciesCategory: item.category || '' });
  };

  const addSpecies = async () => {
    const commonName = newSpecies.commonName.trim();
    if (!commonName || !newSpecies.category || !newSpecies.harvestCategory) {
      setError('Enter a name, species category and default harvest category.');
      return;
    }
    const slug = commonName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!slug) { setError('Use the full Species catalogue to enter a stable species ID for this name.'); return; }
    setCreateBusy(true);
    setError('');
    try {
      const result = await createSpecies({ ...newSpecies, commonName, slug });
      handleSelect(result.data);
      setCreating(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not add the species. Your entry has been kept.');
    } finally {
      setCreateBusy(false);
    }
  };

  const handleQuickPick = async (name) => {
    selectedNameRef.current = '';
    onSelect({ speciesId: null, speciesName: name });
    setInputValue(name);
    setSuggestions([]);
    setOpen(false);
    setError('');
    try {
      const result = await searchSpecies(name, { limit: 50 });
      const hit = (result.data || []).find((species) => species.commonName.toLowerCase() === name.toLowerCase());
      if (hit) handleSelect(hit);
      else {
        selectedNameRef.current = '';
        onSelect({ speciesId: null, speciesName: name });
        setError('This shortcut no longer matches an active catalogue species. Search for its current name.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load this species shortcut. Please retry.');
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIdx]);
    } else if (e.key === 'Enter' && activeIdx < 0 && suggestions.length > 0) {
      e.preventDefault();
      handleSelect(suggestions[0]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleBlur = (e) => {
    if (wrapRef.current && !wrapRef.current.contains(e.relatedTarget)) {
      if (inputValue.trim() === selectedNameRef.current) {
        setOpen(false);
        return;
      }
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = setTimeout(() => {
        setOpen(false);
        // Auto-confirm if there is exactly one suggestion or an exact name match
        if (suggestions.length === 1) {
          handleSelect(suggestions[0]);
          return;
        }
        if (suggestions.length > 1) {
          const exact = suggestions.find(
            (s) => s.commonName.toLowerCase() === inputValue.trim().toLowerCase()
          );
          if (exact) { handleSelect(exact); return; }
          // Pick first suggestion if input matches it closely
          if (inputValue.trim()) handleSelect(suggestions[0]);
          return;
        }
        if (inputValue.trim() && suggestions.length === 0) {
          selectedNameRef.current = inputValue.trim();
          onSelect({ speciesId: null, speciesName: inputValue.trim() });
        }
      }, 180);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} onBlur={handleBlur}>
      <input
        id={id}
        disabled={disabled || createBusy}
        type="text"
        value={inputValue}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (inputValue.trim().length >= 2) setOpen(true); }}
        placeholder={placeholder}
        autoComplete="off"
        style={{
          display: 'block', width: '100%', padding: '8px 10px',
          border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
        }}
      />

      {/* Suggestions dropdown */}
      {open && (suggestions.length > 0 || loading || inputValue.trim().length >= 2) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,.12)', zIndex: 3000,
          maxHeight: 260, overflowY: 'auto',
        }}>
          {loading && <div style={{ padding: '8px 12px', fontSize: 13, color: '#94a3b8' }}>Searching…</div>}
          {!loading && suggestions.map((s, i) => (
            <div
              key={s._id}
              onMouseDown={() => handleSelect(s)}
              style={{
                padding: '9px 12px', cursor: 'pointer', fontSize: 13,
                background: i === activeIdx ? '#f0fdf4' : '#fff',
                borderBottom: i < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
              }}
            >
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{s.commonName}</span>
              {s.communityName && <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>{s.communityName}</span>}
              <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>{s.category}</span>
              {s.isGeneralResource && <span style={{ color: '#64748b', marginLeft: 4, fontSize: 11 }}>(general)</span>}
            </div>
          ))}
          {!loading && suggestions.length === 0 && inputValue.trim().length >= 2 && (
            <div style={{ padding: '9px 12px', fontSize: 13, color: '#6b7280' }}>
              No catalogue matches. Select a catalogue species before saving a guided harvest.
            </div>
          )}

        </div>
      )}

      {/* Popular quick-picks */}
      {error && <p role="alert">{error}</p>}
      {canCreate && !creating && <button type="button" className="species-autocomplete__create" disabled={disabled} onClick={() => {
        clearTimeout(blurTimerRef.current);
        setCreating(true);
        setOpen(false);
        setError('');
        setNewSpecies({ commonName: inputValue.trim(), category: '', harvestCategory: '' });
      }}>+ New species</button>}
      {canCreate && creating && <fieldset disabled={createBusy || disabled} style={{ marginTop: 8 }} onKeyDown={(event) => {
        if (event.key === 'Enter') event.preventDefault();
      }}>
        <legend>Add to shared species catalogue</legend>
        <p>Search existing names first. This entry is shared across communities; your current harvest or guidance draft stays open.</p>
        <label>New species name<input value={newSpecies.commonName} onChange={(event) => setNewSpecies({ ...newSpecies, commonName: event.target.value })} /></label>
        <label>Species category<select value={newSpecies.category} onChange={(event) => setNewSpecies({ ...newSpecies, category: event.target.value })}>
          <option value="">Choose classification</option>
          {SPECIES_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
        </select></label>
        <label>Default harvest category<select value={newSpecies.harvestCategory} onChange={(event) => setNewSpecies({ ...newSpecies, harvestCategory: event.target.value })}>
          <option value="">Choose collection default</option>
          <option value="fishing">Fishing</option><option value="hunting">Hunting</option><option value="birds">Birds</option>
          <option value="plants_medicine">Plants &amp; Medicine</option><option value="environmental">Environmental</option><option value="other">Other</option>
        </select></label>
        <button type="button" onClick={addSpecies}>{createBusy ? 'Adding...' : 'Add and select species'}</button>
        <button type="button" onClick={() => { setCreating(false); setError(''); }}>Cancel new species</button>
      </fieldset>}
      {popularSpecies.length > 0 && (
        <div className="species-autocomplete__popular">
          {popularSpecies.slice(0, 8).map((name) => (
            <button
              key={name}
              type="button"
              disabled={disabled}
              onClick={() => handleQuickPick(name)}
              style={{
                fontSize: 12, border: `1px solid ${POPULAR_BORDER}`, background: POPULAR_COLOR,
                borderRadius: 999, padding: '3px 10px', cursor: 'pointer', color: '#334155',
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
